import { useQuery } from '@tanstack/react-query';
import type { SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import type { AppObjectiveTemplate } from '@/lib/student-objectives/objective-types';
import { ObjectiveCard } from './objective-card';

interface Props {
  studentLevel: SchoolLevel;
  selectedIds: string[];
  onToggle: (templateId: string, template: AppObjectiveTemplate) => void;
  maxSelections?: number;
}

export function ObjectivePicker({ studentLevel, selectedIds, onToggle, maxSelections = 5 }: Props) {
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['objective-templates', studentLevel],
    queryFn: async (): Promise<AppObjectiveTemplate[]> => {
      const { data, error } = await supabase
        .from('objective_templates')
        // `*` : `requires_pack_selection` (0514) est postérieur aux types générés.
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;

      return (
        (data ?? [])
          // L'objectif Diagnostic est auto-assigné (épinglé) → jamais dans le
          // sélecteur manuel.
          .filter((t) => t.type !== 'diagnostic')
          .filter((t) => {
            const lvls = t.allowed_levels as string[];
            return lvls.length === 0 || lvls.includes(studentLevel);
          })
          .map((t) => ({
            id: t.id,
            title: t.title,
            description: t.description,
            type: t.type,
            allowedLevels: t.allowed_levels as AppObjectiveTemplate['allowedLevels'],
            subject: t.subject as AppObjectiveTemplate['subject'],
            filters: (t.filters ?? {}) as Record<string, unknown>,
            // `requires_pack_selection` (0514) est postérieur aux types générés.
            requiresPackSelection:
              (t as { requires_pack_selection?: boolean }).requires_pack_selection ?? false,
            contentType: (t.content_type ?? 'exercises') as 'exercises' | 'epreuves',
            isActive: t.is_active,
            displayOrder: t.display_order,
            illustrationUrl: (t as { illustration_url?: string | null }).illustration_url ?? null,
            defaultDeadline: (t as { default_deadline?: string | null }).default_deadline ?? null,
          }))
      );
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid animate-pulse grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-ink-100 h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  const atMax = selectedIds.length >= maxSelections;

  return (
    <div className="space-y-3">
      {maxSelections > 1 && (
        <p className="text-ink-400 text-xs">
          {selectedIds.length}/{maxSelections} sélectionnés
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {templates.map((t) => {
          const isSelected = selectedIds.includes(t.id);
          const isDisabled = !isSelected && atMax;
          return (
            <ObjectiveCard
              key={t.id}
              template={t}
              isSelected={isSelected}
              isDisabled={isDisabled}
              onSelect={() => {
                if (!isSelected && atMax) return;
                onToggle(t.id, t);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
